import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatErr, compressImage, STATUS_STYLES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Camera, CheckCircle2, XCircle, AlarmClock, ArrowUpRight, LayoutGrid, List } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { ActionsBoard } from "../components/ActionsBoard";
import { toast } from "sonner";

const TABS = ["all", "open", "in_progress", "awaiting_review", "closed", "rejected"];

export default function Actions() {
  const { user } = useAuth();
  const canReview = ["corporate_admin", "regional_head", "external_auditor"].includes(user.role);
  const [actions, setActions] = useState([]);
  const [tab, setTab] = useState("all");
  const [searchParams] = useSearchParams();
  const [hotelFilter, setHotelFilter] = useState(searchParams.get("hotel") || "all");
  const [auditFilter, setAuditFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [progress, setProgress] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [checked, setChecked] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkComment, setBulkComment] = useState("");
  const [view, setView] = useState(searchParams.get("hotel") ? "list" : "board");
  const isAdmin = user.role === "corporate_admin";
  const fileRef = useRef();

  const load = () => api.get("/actions").then((r) => setActions(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = (a) => {
    setSel(a);
    setProgress(a.progress || 0);
    setReviewComment("");
  };

  const update = async (patch) => {
    try {
      const { data } = await api.put(`/actions/${sel.id}`, patch);
      setSel(data);
      load();
      return data;
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const addEvidence = async (file) => {
    if (!file) return;
    const dataUrl = await compressImage(file);
    await update({ evidence: [...(sel.evidence || []), { data: dataUrl, at: new Date().toISOString(), by: user.name }] });
    toast.success("Evidence uploaded");
  };

  const review = async (approve) => {
    try {
      await api.post(`/actions/${sel.id}/review`, { approve, comment: reviewComment });
      toast.success(approve ? "Action approved & closed" : "Action rejected — sent back");
      setSel(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const hotelNames = [...new Set(actions.map((a) => a.hotel_name))].sort();
  const auditNames = [...new Set(actions.map((a) => a.audit_name).filter(Boolean))].sort();
  const scoped = actions.filter((a) => (hotelFilter === "all" || a.hotel_name === hotelFilter) && (auditFilter === "all" || a.audit_name === auditFilter));
  const filtered = tab === "all" ? scoped : scoped.filter((a) => a.status === tab);
  const counts = TABS.reduce((m, t) => ({ ...m, [t]: t === "all" ? scoped.length : scoped.filter((a) => a.status === t).length }), {});
  const closable = filtered.filter((a) => a.status !== "closed").map((a) => a.id);
  const allChecked = closable.length > 0 && closable.every((id) => checked.includes(id));

  const toggleCheck = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const bulkClose = async () => {
    try {
      const { data } = await api.post("/actions/bulk-close", { ids: checked, comment: bulkComment });
      toast.success(`${data.closed} action${data.closed === 1 ? "" : "s"} closed`);
      setChecked([]);
      setBulkOpen(false);
      setBulkComment("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="actions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Corrective Actions</h1>
          <p className="text-stone-500 mt-1 text-sm">Every non-compliance becomes a trackable action item.</p>
        </div>
        <div className="inline-flex rounded-lg border border-[#E7E5DF] bg-white p-0.5">
          <button data-testid="actions-view-board" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${view === "board" ? "bg-[#14532D] text-white" : "text-stone-600 hover:bg-stone-50"}`} onClick={() => setView("board")}>
            <LayoutGrid className="w-3.5 h-3.5" />By Hotel
          </button>
          <button data-testid="actions-view-list" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${view === "list" ? "bg-[#14532D] text-white" : "text-stone-600 hover:bg-stone-50"}`} onClick={() => setView("list")}>
            <List className="w-3.5 h-3.5" />List
          </button>
        </div>
      </div>

      {view === "board" && <ActionsBoard onPickHotel={(name) => { setHotelFilter(name); setTab("all"); setView("list"); }} />}

      {view === "list" && (<>
      <div className="flex flex-wrap items-center gap-3">
        <div className="overflow-x-auto max-w-full">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-[#E7E5DF]">
              {TABS.map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`actions-tab-${t}`} className="capitalize data-[state=active]:bg-[#14532D] data-[state=active]:text-white">
                  {t.replace("_", " ")} <span className="ml-1.5 font-mono text-[10px] opacity-70">{counts[t]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <Select value={hotelFilter} onValueChange={setHotelFilter}>
          <SelectTrigger className="w-52 h-9 text-sm bg-white" data-testid="actions-hotel-filter"><SelectValue placeholder="Hotel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All hotels</SelectItem>
            {hotelNames.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={auditFilter} onValueChange={setAuditFilter}>
          <SelectTrigger className="w-52 h-9 text-sm bg-white" data-testid="actions-audit-filter"><SelectValue placeholder="Audit" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All audits</SelectItem>
            {auditNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isAdmin && checked.length > 0 && (
        <div className="flex items-center gap-3 bg-[#0F2E1B] text-white rounded-xl px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-medium">{checked.length} selected</span>
          <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#0F2E1B] font-semibold" data-testid="bulk-close-btn" onClick={() => setBulkOpen(true)}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" />Close selected
          </Button>
          <button className="text-xs text-white/60 hover:text-white underline" data-testid="bulk-clear" onClick={() => setChecked([])}>Clear</button>
        </div>
      )}

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-stone-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              {isAdmin && (
                <th className="py-3 pl-4 pr-0 w-8">
                  <Checkbox data-testid="bulk-select-all" checked={allChecked} onCheckedChange={(v) => setChecked(v ? closable : [])} />
                </th>
              )}
              <th className="py-3 px-4">Finding</th>
              <th className="py-3 px-4">Hotel</th>
              <th className="py-3 px-4">Department</th>
              <th className="py-3 px-4">Responsible</th>
              <th className="py-3 px-4">Priority</th>
              <th className="py-3 px-4">Due</th>
              <th className="py-3 px-4">Progress</th>
              <th className="py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody data-testid="actions-table">
            {filtered.map((a) => (
              <tr key={a.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60 cursor-pointer" onClick={() => openDetail(a)} data-testid={`action-row-${a.id}`}>
                {isAdmin && (
                  <td className="py-3.5 pl-4 pr-0 w-8" onClick={(e) => e.stopPropagation()}>
                    {a.status !== "closed" && (
                      <Checkbox data-testid={`action-check-${a.id}`} checked={checked.includes(a.id)} onCheckedChange={() => toggleCheck(a.id)} />
                    )}
                  </td>
                )}
                <td className="py-3.5 px-4 text-sm text-stone-800 max-w-[260px]">
                  <p className="truncate">{a.description}</p>
                  {a.overdue && <span className="inline-flex items-center gap-1 mr-2 text-[10px] font-bold text-rose-600 uppercase"><AlarmClock className="w-3 h-3" />Overdue</span>}
                  {a.escalated && <span data-testid={`action-escalated-${a.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-rose-600 px-1.5 py-0.5 rounded uppercase"><ArrowUpRight className="w-3 h-3" />Escalated</span>}
                </td>
                <td className="py-3.5 px-4 text-sm text-stone-600 max-w-[160px] truncate">{a.hotel_name}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{a.department}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{a.responsible_name}</td>
                <td className="py-3.5 px-4">
                  <span className={`text-[11px] font-semibold uppercase ${a.priority === "high" ? "text-rose-600" : a.priority === "medium" ? "text-amber-600" : "text-stone-500"}`}>{a.priority}</span>
                </td>
                <td className="py-3.5 px-4 text-sm font-mono text-stone-600">{a.due_date?.slice(0, 10)}</td>
                <td className="py-3.5 px-4 w-28">
                  <div className="h-1.5 bg-stone-100 rounded-full"><div className="h-1.5 bg-[#14532D] rounded-full" style={{ width: `${a.progress || 0}%` }} /></div>
                </td>
                <td className="py-3.5 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[a.status]}`}>{a.status.replace("_", " ")}</span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="py-12 text-center text-sm text-stone-400">No actions in this state.</td></tr>}
          </tbody>
        </table>
      </div>
      </>)}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-close-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Close {checked.length} corrective action{checked.length === 1 ? "" : "s"}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">They will be marked closed with your name on the review trail, and each responsible person will be notified.</p>
          <Textarea data-testid="bulk-close-comment" placeholder="Closing comment (optional)" value={bulkComment} onChange={(e) => setBulkComment(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="bg-[#14532D] hover:bg-[#0F2E1B]" data-testid="bulk-close-confirm" onClick={bulkClose}>
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Close actions
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="pr-6 leading-snug">{sel.description}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <p className="text-stone-500">Hotel <span className="block text-stone-800 font-medium">{sel.hotel_name}</span></p>
                <p className="text-stone-500">Department <span className="block text-stone-800 font-medium">{sel.department}</span></p>
                <p className="text-stone-500">Responsible <span className="block text-stone-800 font-medium">{sel.responsible_name}</span></p>
                <p className="text-stone-500">Due <span className="block text-stone-800 font-medium font-mono">{sel.due_date?.slice(0, 10)}</span></p>
              </div>
              {sel.finding && <p className="text-sm bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-rose-800">Auditor finding: {sel.finding}</p>}

              {["open", "in_progress", "rejected"].includes(sel.status) && (
                <div className="space-y-4 pt-2 border-t">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-stone-500 mb-2">Progress: {progress}%</p>
                    <Slider value={[progress]} max={100} step={5} onValueChange={(v) => setProgress(v[0])} data-testid="action-progress-slider" />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="action-evidence-button">
                      <Camera className="w-3.5 h-3.5 mr-1.5" />Upload evidence
                    </Button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { addEvidence(e.target.files[0]); e.target.value = ""; }} />
                    <Button size="sm" variant="outline" onClick={async () => { await update({ status: "in_progress", progress }); toast.success("Updated"); }} data-testid="action-update-button">
                      Save update
                    </Button>
                    <Button size="sm" className="bg-[#14532D] hover:bg-[#166534]" onClick={async () => { await update({ status: "awaiting_review", progress: 100 }); toast.success("Submitted for review"); }} data-testid="action-submit-review-button">
                      Submit for review
                    </Button>
                  </div>
                </div>
              )}

              {(sel.evidence || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-stone-500 mb-2">Evidence</p>
                  <div className="flex gap-2 flex-wrap">
                    {sel.evidence.map((ev, i) => <img key={`${ev.at || ev.by || "ev"}-${i}`} src={ev.data} alt="evidence" className="w-20 h-20 object-cover rounded-lg border" />)}
                  </div>
                </div>
              )}

              {canReview && sel.status === "awaiting_review" && (
                <div className="space-y-3 pt-2 border-t">
                  <p className="text-xs font-medium uppercase tracking-wider text-stone-500">Review</p>
                  <Textarea rows={2} placeholder="Reviewer comment..." value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} data-testid="review-comment-input" />
                  <div className="flex gap-3">
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => review(true)} data-testid="review-approve-button">
                      <CheckCircle2 className="w-4 h-4 mr-2" />Approve & Close
                    </Button>
                    <Button variant="outline" className="flex-1 text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => review(false)} data-testid="review-reject-button">
                      <XCircle className="w-4 h-4 mr-2" />Reject
                    </Button>
                  </div>
                </div>
              )}

              {(sel.reviewer_comments || []).length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <p className="text-xs font-medium uppercase tracking-wider text-stone-500">Review history</p>
                  {sel.reviewer_comments.map((c, i) => (
                    <p key={`${c.at || "rc"}-${i}`} className="text-xs text-stone-600 bg-stone-50 rounded-lg px-3 py-2">
                      <b>{c.by}</b> — {c.decision} {c.comment && `· "${c.comment}"`} <span className="text-stone-400">({new Date(c.at).toLocaleDateString()})</span>
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
